import { WeblabCache } from '../provider/weblabs_provider'
import * as O from 'fp-ts/es6/Option'
import * as A from 'fp-ts/es6/Array'
import * as M from 'fp-ts/es6/Map'
import { pipe, tuple } from 'fp-ts/es6/function'
import { eqString } from 'fp-ts/es6/Eq'
import { WeblabCacheEntry, WeblabTreatment } from '../types/weblab-types'

const PREFIX = 'aapn_weblab_'

type StorageListItem = { weblab: string; value: string }

const getStorageList = (storage: Storage): StorageListItem[] => {
  const result: StorageListItem[] = []
  for (let index = 0; index < storage.length; index++) {
    const weblab = storage.key(index)
    const value = weblab ? storage.getItem(weblab) : null
    if (weblab && value && weblab.startsWith(PREFIX)) {
      // guaranteed but types don't say so
      result.push({ weblab, value })
    }
  }

  return result
}

export const createLocalStorageWeblabCache = (getStorage: () => Storage): WeblabCache => {
  const storage = getStorage()

  const getCurrentWeblabsMap = (): Map<string, WeblabCacheEntry> =>
    pipe(
      getStorageList(storage),
      A.filterMap(e =>
        pipe(
          O.tryCatch(() => JSON.parse(e.value)),
          O.chain(o => O.fromEither(WeblabCacheEntry.decode(o))),
        ),
      ),
      A.map(e => tuple(e.weblab + '_' + e.principal, e)),
      M.fromFoldable(
        eqString,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        { concat: (a: WeblabCacheEntry, _b: WeblabCacheEntry) => a },
        A.array,
      ),
    )

  return {
    get: (principal: string, weblab: string): O.Option<WeblabTreatment> => {
      const currentWeblabsMap = getCurrentWeblabsMap()
      return pipe(
        O.fromNullable(currentWeblabsMap.get(weblab + '_' + principal)),
        O.map(cachedValue => ({ weblab: cachedValue.weblab, treatment: cachedValue.treatment })),
      )
    },
    put: (principal: string, weblabTreatment: WeblabTreatment): void => {
      const cacheEntry: WeblabCacheEntry = {
        principal,
        weblab: weblabTreatment.weblab,
        treatment: weblabTreatment.treatment,
      }

      storage.setItem(PREFIX + weblabTreatment.weblab + '_' + principal, JSON.stringify(cacheEntry))
    },
  }
}

export const LocalStorageWeblabCache = createLocalStorageWeblabCache(() => window.localStorage)
